import {Link} from 'react-router-dom';
import classNames from 'classnames';
import {Alert} from 'react-bootstrap';
import {loginValidationSchema} from 'form/validation/login';
import {useAuth} from 'context/auth-context';
import {createMaps, parseJwt} from 'helpers/common';

import {useLogin} from 'api/users';

import SubmitButton from 'components/ui/Button/SubmitButton';
import UnauthorizedPageTitle from 'components/ui/UnauthorizedPageTitle/UnauthorizedPageTitle';
import {PATH} from 'constants/common';
import TextInput from 'components/ui/Form/TextInput/TextInput';
import Form, {FormOnSubmit} from 'components/ui/Form/Form/Form';
import IconSVG from 'components/ui/Icon/IconSVG';
import {LayoutVariants} from 'components/layout/UnauthorisedLayout/UnauthorisedLayout';
import {translate} from 'helpers/localize';
import {useLanguage} from 'context/language-context';
import {SEO_TEXTS} from 'constants/seo';
import MaintenanceMessage from 'components/ui/MaintenanceMessage/MaintenanceMessage';
import {useResolveInviteStore} from 'components/pages/ResolveInvitePage/resolveInviteStore';
//@ts-ignore
import {API_URL} from '../../../config';

import styles from './LoginPage.module.scss';
import {useGetTwoFALogin} from 'api/futurae';
import {useFingerprintStore} from 'stores/fingerprintStore';
import {useEffect, useState} from 'react';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';

export interface ILoginForm {
    email: string;
    password: string;
}

const VARIANT_MAP = createMaps<Record<LayoutVariants, string>>({
    [LayoutVariants.DEFAULT]: PATH.REGISTRATION,
    [LayoutVariants.WIR]: PATH.REGISTRATION_WIR,
    [LayoutVariants.A352]: PATH.REGISTRATION_A352,
    [LayoutVariants.BEXIO]: PATH.REGISTRATION_BEXIO,
    [LayoutVariants.WIENER_PRIVATBANK]: PATH.REGISTRATION_WIENER_PRIVATBANK,
    [LayoutVariants.TWENTYFOUR_SEVEN]: PATH.REGISTRATION_TWENTYFOUR_SEVEN,
});

export interface ILoginPageProps {
    variant?: LayoutVariants;
}

const LoginPage = ({variant = LayoutVariants.DEFAULT}: ILoginPageProps) => {
    const {login, loginScreenErrorMessage, setToken} = useAuth();
    const {selectedLanguageParams} = useLanguage();
    const [recaptchaLoading, setRecaptchaLoading] = useState<boolean>(false);
    const hash = useResolveInviteStore(state => state.hash);
    const type = useResolveInviteStore(state => state.type);
    const setInvite = useResolveInviteStore(state => state.setInvite);

    const {mutate: authenticate, isPending: isLoadingLogin} = useLogin();
    const {mutate: authenticate2FA} = useGetTwoFALogin();
    const fingerprintRequestId = useFingerprintStore(
        state => state.fingerprintRequestId,
    );
    const isFingerprintLoading = useFingerprintStore(state => state.isLoading);
    const getFingerprintData = useFingerprintStore(
        state => state.getFingerprintData,
    );
    const {executeRecaptcha} = useGoogleReCaptcha();

    useEffect(() => {
        getFingerprintData();
    }, []);

    const onSubmit: FormOnSubmit<ILoginForm> = async (values, setError) => {
        let captcha: string | undefined;
        try {
            setRecaptchaLoading(true);
            captcha = await executeRecaptcha?.('registration');
        } finally {
            setRecaptchaLoading(false);
        }
        const request = {
            ...values,
            fingerprintRequestId,
            businessPartnerInviteHash:
                type === 'business-partner' ? hash! : undefined,
            captcha: captcha,
        };
        authenticate(request, {
            onSuccess(data) {
                if (!data) {
                    setError('formError', {
                        message: 'Something went wrong, please try again later',
                    });
                }
                const decodedToken = parseJwt(data.token);

                if (data.inviteHashes.length) {
                    setInvite({
                        hash: data.inviteHashes[0],
                        type: 'contact',
                    });
                }

                if (decodedToken.needs_2fa) {
                    setToken(data.token);
                    authenticate2FA(
                        {token: data.token},
                        {
                            onSuccess: response => {
                                return login(response.token);
                            },
                        },
                    );
                } else {
                    login(data.token);
                }
            },
            onError: (errors: any) => {
                setError('formError', {
                    message:
                        errors.message ||
                        errors['hydra:description'] ||
                        'Something went wrong',
                });
            },
        });
    };

    return (
        <>
            <UnauthorizedPageTitle
                pageTitle={<span>Login</span>}
                subtitle={translate('We are pleased to see you’re back.')}
                titleTag={SEO_TEXTS[selectedLanguageParams.code]?.login?.title}
                metaTitle={SEO_TEXTS[selectedLanguageParams.code]?.login?.title}
                metaDescription={
                    SEO_TEXTS[selectedLanguageParams.code]?.login?.description
                }
                metaKeywords={
                    SEO_TEXTS[selectedLanguageParams.code]?.login?.keywords
                }
            />
            <Form<ILoginForm>
                onSubmit={onSubmit}
                validationSchema={loginValidationSchema}
            >
                <TextInput
                    label="Business Email"
                    name="email"
                    placeholder="What is your business email?"
                    autoComplete="email"
                    rounded={false}
                    size="large"
                />
                <TextInput
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    rounded={false}
                    size="large"
                />
                {loginScreenErrorMessage ? (
                    <Alert variant="danger" className={styles.alert}>
                        {loginScreenErrorMessage}
                    </Alert>
                ) : null}
                <MaintenanceMessage />
                <div className={styles.buttonContainer}>
                    <SubmitButton
                        className={styles.submitBtn}
                        isLoading={isLoadingLogin || isFingerprintLoading}
                        disabled={
                            isLoadingLogin ||
                            window.__env__.MAINTENANCE_ACTIVE === '1'
                        }
                    >
                        LOGIN{' '}
                        <IconSVG
                            name="upArrow"
                            customDimensions
                            className="ml-2"
                        />
                    </SubmitButton>
                    <a
                        href={`${API_URL}/web_api/bexio/login`}
                        type="button"
                        className={classNames(
                            styles.submitBtn,
                            'btn btn-outline-primary',
                            {
                                [styles.disabled]:
                                    window.__env__.MAINTENANCE_ACTIVE === '1',
                            },
                        )}
                    >
                        Login/Register with bexio
                    </a>
                </div>
            </Form>
            <div className="mt-3">
                <p>
                    Don’t have an account yet?&nbsp;
                    <Link className="ml-1" to={VARIANT_MAP[variant]}>
                        Create your account here
                    </Link>
                </p>
                <p>
                    Forgot password?&nbsp;
                    <Link className="ml-1" to={PATH.FORGOT_PASSWORD}>
                        Click here
                    </Link>
                </p>
            </div>
        </>
    );
};

export default LoginPage;
